import { MessageDescriptor } from 'react-intl';

import { REGEX_RULES } from '@savgroup-front-common/constants/shared';
import { removeAccent } from '@savgroup-front-common/core/formatters/removeAccent';
import { isEmail } from '@savgroup-front-common/core/helpers';
import { BaseAutocompleteOptionForHookForm } from '@savgroup-front-common/types';
import {
  COMPANY_DIRECTORY_SOURCE,
  CompanyDirectorySource,
} from '@savgroup-front-common/types/CompanyDirectory';

import messages from './messages';
import {
  CNAME_FALLBACK_PREFIX,
  CNAME_MAX_LENGTH,
  CNAME_MIN_LENGTH,
  CNAME_PATTERN,
} from './OnboardingForm.constants';

const CONSUMER_OR_INTERNAL_EMAIL_DOMAINS = new Set([
  'aol.com',
  'free.fr',
  'gmail.com',
  'googlemail.com',
  'hotmail.com',
  'icloud.com',
  'laposte.net',
  'live.com',
  'msn.com',
  'orange.fr',
  'outlook.com',
  'proton.me',
  'protonmail.com',
  'revers.io',
  'sfr.fr',
  'wanadoo.fr',
  'yahoo.com',
  'yahoo.fr',
]);

export function slugifyCompanyName(companyName?: string): string | undefined {
  const slug = removeAccent(companyName ?? '')
    .toLowerCase()
    .replace(REGEX_RULES.NON_ALPHANUMERIC, '-')
    .replace(REGEX_RULES.EDGE_DASHES, '')
    .slice(0, CNAME_MAX_LENGTH)
    .replace(REGEX_RULES.EDGE_DASHES, '');

  if (!slug) {
    return undefined;
  }

  return slug.length >= CNAME_MIN_LENGTH
    ? slug
    : `${CNAME_FALLBACK_PREFIX}-${slug}`;
}

export const isValidCName = (value?: string): boolean =>
  !!value &&
  value.length >= CNAME_MIN_LENGTH &&
  value.length <= CNAME_MAX_LENGTH &&
  CNAME_PATTERN.test(value);

export function getDomainSuffix({
  cName,
  domainName,
}: {
  cName: string;
  domainName: string;
}): string {
  return domainName.startsWith(cName)
    ? domainName.slice(cName.length)
    : domainName;
}

export function buildCNameCandidate({
  slug,
  attempt,
}: {
  slug: string;
  attempt: number;
}): string {
  if (attempt === 0) {
    return slug;
  }

  const suffix = `-${attempt + 1}`;

  return `${slug.slice(0, CNAME_MAX_LENGTH - suffix.length).replace(REGEX_RULES.EDGE_DASHES, '')}${suffix}`;
}

export const findCountryOption = ({
  countryOptions,
  countryCode,
}: {
  countryOptions: BaseAutocompleteOptionForHookForm[];
  countryCode?: string;
}): BaseAutocompleteOptionForHookForm | undefined =>
  countryCode
    ? countryOptions.find(
        (option) => option.value.toUpperCase() === countryCode.toUpperCase(),
      )
    : undefined;

export function getSourceMessage(
  source: CompanyDirectorySource,
): MessageDescriptor | undefined {
  switch (source) {
    case COMPANY_DIRECTORY_SOURCE.FRENCH_COMPANY_REGISTRY:
      return messages.sourceFrenchCompanyRegistry;
    case COMPANY_DIRECTORY_SOURCE.COMPANIES_HOUSE:
      return messages.sourceCompaniesHouse;
    case COMPANY_DIRECTORY_SOURCE.BELGIAN_CROSSROADS_BANK:
      return messages.sourceBelgianCrossroadsBank;
    case COMPANY_DIRECTORY_SOURCE.GLEIF:
      return messages.sourceGleif;
    default:
      source satisfies never;

      return undefined;
  }
}

export function prepareEmailForCompanyProfile(
  email: string | undefined,
): string | undefined {
  const trimmed = email?.trim().toLowerCase();

  if (!trimmed || !isEmail(trimmed)) {
    return undefined;
  }

  const domain = trimmed.split('@').at(1);

  if (!domain || CONSUMER_OR_INTERNAL_EMAIL_DOMAINS.has(domain)) {
    return undefined;
  }

  return trimmed;
}
